import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { MessageCircle, Menu, X } from "lucide-react";
import { scrollToId, waLink } from "@/lib/content";

const NAV = [
  { label: "Villas", id: "villas" },
  { label: "Story", id: "story" },
  { label: "Why Us", id: "why-us" },
  { label: "Experiences", id: "experiences" },
  { label: "Reviews", id: "reviews" },
];

export const Header = ({ config }) => {
  const [open, setOpen] = useState(false);
  const wa = waLink(config.whatsapp_number, config.whatsapp_default_message);

  return (
    <>
      <header
        data-testid="nav-header"
        className="fixed top-0 left-0 right-0 z-50 border-b border-stone-900/10 bg-ivory/85 backdrop-blur-md"
      >
        <div className="mx-auto flex h-16 max-w-7xl items-center justify-between px-5 sm:px-8">
          <button
            data-testid="brand-wordmark"
            onClick={() => window.__lenis?.scrollTo(0, { duration: 1.4 })}
            className="font-serif text-lg tracking-[0.22em] text-ink sm:text-xl"
          >
            BALIBIG<span className="text-gold">VILLAS</span>
          </button>

          <nav className="hidden items-center gap-8 lg:flex" data-testid="nav-links">
            {NAV.map((item) => (
              <button
                key={item.id}
                data-testid={`nav-link-${item.id}`}
                onClick={() => scrollToId(item.id)}
                className="link-lux text-[11px] font-medium uppercase tracking-[0.2em] text-stone-600 transition-colors duration-300 hover:text-ink"
              >
                {item.label}
              </button>
            ))}
          </nav>

          <div className="flex items-center gap-3">
            <a
              data-testid="nav-whatsapp-cta"
              href={wa}
              target="_blank"
              rel="noopener noreferrer"
              className="hidden items-center gap-2.5 border border-ink/80 px-5 py-2.5 text-[11px] font-medium uppercase tracking-[0.18em] text-ink transition-colors duration-500 hover:bg-ink hover:text-ivory sm:flex"
            >
              <span className="relative flex h-1.5 w-1.5">
                <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-[#25D366] opacity-60" />
                <span className="relative inline-flex h-1.5 w-1.5 rounded-full bg-[#25D366]" />
              </span>
              WhatsApp
            </a>
            <button
              data-testid="mobile-menu-toggle"
              onClick={() => setOpen(true)}
              className="p-2 text-ink lg:hidden"
              aria-label="Open menu"
            >
              <Menu size={22} strokeWidth={1.5} />
            </button>
          </div>
        </div>
      </header>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu-overlay"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.4 }}
            className="fixed inset-0 z-[60] flex flex-col bg-ivory"
          >
            <div className="flex h-16 items-center justify-between border-b border-stone-900/10 px-5">
              <span className="font-serif text-lg tracking-[0.22em]">
                BALIBIG<span className="text-gold">VILLAS</span>
              </span>
              <button
                data-testid="mobile-menu-close"
                onClick={() => setOpen(false)}
                className="p-2"
                aria-label="Close menu"
              >
                <X size={24} strokeWidth={1.5} />
              </button>
            </div>
            <nav className="flex flex-1 flex-col justify-center gap-2 px-8">
              {NAV.map((item, i) => (
                <motion.button
                  key={item.id}
                  data-testid={`mobile-nav-link-${item.id}`}
                  initial={{ opacity: 0, x: -24 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.08 * i, duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                  onClick={() => {
                    setOpen(false);
                    setTimeout(() => scrollToId(item.id), 350);
                  }}
                  className="border-b border-stone-900/10 py-5 text-left font-serif text-4xl font-light text-ink"
                >
                  {item.label}
                </motion.button>
              ))}
              <motion.a
                data-testid="mobile-menu-whatsapp-cta"
                initial={{ opacity: 0, y: 16 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.55, duration: 0.5 }}
                href={wa}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-8 flex items-center justify-center gap-3 bg-ink py-4 text-xs font-medium uppercase tracking-[0.2em] text-ivory"
              >
                <MessageCircle size={16} />
                Inquire via WhatsApp
              </motion.a>
            </nav>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
};
